import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { Brain, Zap, Search, Plug, Play, Database, Code, Globe, Terminal, Download } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function ClaudeDesktopLanding() {
  const { t } = useTranslation('integrations');
  const handleGetStarted = () => {
    window.location.href = '/login';
  };
  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=ouW739Hx7v8', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="claude_desktop"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 shadow" aria-label="Claude logo">
          <Icon icon="simple-icons:anthropic" width={48} height={48} />
        </span>
      }
      title="Claude Integration"
      description="Enterprise AI assistant with persistent memory for Claude Desktop and claude.ai. Use the MCP URL Memara shows for Settings → Connectors in the app or on the web (Streamable HTTP, OAuth on the MCP host, tools served on /mcp)."
      features={[
        { icon: <Globe className="h-4 w-4 text-indigo-600" />, title: 'Claude Desktop and claude.ai', description: 'Add Memara as a custom connector with the MCP URL from your integration setup; works in the desktop app and in the browser (Connectors + OAuth).' },
        { icon: <Plug className="h-4 w-4 text-indigo-600" />, title: 'MCP Protocol Integration', description: 'Industry-standard Model Context Protocol for professional AI agent memory operations and enterprise compatibility.' },
        { icon: <Zap className="h-4 w-4 text-indigo-600" />, title: 'Sub-200ms Memory Access', description: 'Lightning-fast memory retrieval and storage with optimized performance for seamless conversational workflows.' },
        { icon: <Brain className="h-4 w-4 text-indigo-600" />, title: 'Persistent Context Memory', description: 'Never lose conversational context—Claude maintains intelligent memory across all research sessions and interactions.' },
        { icon: <Search className="h-4 w-4 text-indigo-600" />, title: 'Research-Focused Intelligence', description: 'Optimized for deep research workflows, knowledge work, and continuous learning with semantic memory operations.' },
        { icon: <Database className="h-4 w-4 text-indigo-600" />, title: 'Knowledge Base Management', description: 'Multi-tenant memory spaces with enterprise-grade security and organized knowledge management capabilities.' },
        { icon: <Code className="h-4 w-4 text-indigo-600" />, title: 'Developer-Friendly Setup', description: 'Simple configuration with comprehensive API documentation and professional development workflow integration.' },
      ]}
      setupSteps={[
        'Create Socket: Set up your secure Memara connection endpoint',
        'Add connector: In Claude Desktop or claude.ai, Settings → Connectors → add the MCP URL Memara shows after you create the integration',
        'Start Enhanced Sessions: Use Claude with persistent memory across sessions',
      ]}
      ctas={[
        {
          label: 'Start 3-Minute Setup',
          onClick: handleGetStarted,
          className: 'bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
          icon: <Plug className="mr-2 h-5 w-5" />,
        },
        {
          label: t('wizard.cta.installWithWizard'),
          onClick: () => { window.location.href = '/wizard'; },
          className: 'border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Terminal className="w-4 h-4 mr-2" />,
        },
        {
          label: t('wizard.cta.downloadSkillZip'),
          onClick: () => { window.location.href = '/skills/memara-memory/memara-memory.zip'; },
          className: 'border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Download className="w-4 h-4 mr-2" />,
        },
        {
          label: 'See Integration Demo',
          onClick: handleSeeDemo,
          className: 'border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'claude-mcp-config',
          title: 'MCP Configuration',
          description: 'Configure Claude (Desktop or web Connectors) with Memara\'s MCP host URL',
          placeholder: 'Claude connector settings showing Memara MCP URL',
          badge: 'Setup'
        },
        {
          id: 'memory-operations',
          title: 'Memory Operations',
          description: 'Create, search, and manage memories within Claude after connecting Memara',
          placeholder: 'Claude chat showing memory creation and search',
          badge: 'In Action'
        },
        {
          id: 'research-workflow',
          title: 'Research Workflow',
          description: 'Persistent context across research sessions with intelligent knowledge building',
          placeholder: 'Claude research conversation with continuous memory context',
          badge: 'Workflow'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=ouW739Hx7v8"
    >
      {/* Custom content can go here if needed */}
    </IntegrationLandingBase>
  );
} 